// Converts a VolleyStation ".vsm" export (plain JSON, already shaped very
// close to DataVolley's own conventions) into the data shape consumed by
// `buildDVWMatchText`, so it can be opened through the same `generateMatch`
// pipeline as a real DataVolley file — mirrors `VISToDVW.js`'s
// `visJsonToMatchData`, just for a different (JSON, not NRBF) source format.
//
// Unlike VIS, VSM already uses DataVolley's own skill letters (S R E A B D F)
// and evaluation characters (# + ! - = /) directly on each "play", so no
// rally-code decoding is needed — each play maps ~1:1 onto a DV scout event.

import { kPowerPlayCodes } from "./Constants";
import { generateUUID } from "./Utils";

function pad2(value) {
  const n = parseInt(String(value), 10);
  return Number.isFinite(n) ? String(n).padStart(2, "0") : "00";
}

// "positions": {"1":5,"2":8,...} -> ["5","8",...] (6 entries, in court-
// position order 1..6; "" for an unfilled slot).
function sixFromPositions(positions) {
  const arr = [];
  for (let p = 1; p <= 6; p++) {
    const v = positions?.[String(p)];
    arr.push(v != null ? String(v) : "");
  }
  return arr;
}

function lineupSnapshotFromRallyLineup(lineup) {
  if (!lineup) return null;
  return {
    A: sixFromPositions(lineup.home?.positions),
    B: sixFromPositions(lineup.away?.positions),
  };
}

function lineupArrayFromPositions(positions) {
  return sixFromPositions(positions).map((v, i) => ({
    shirtNumber: v === "" ? null : parseInt(v, 10),
    position: i + 1,
  }));
}

// "setterAt" is the COURT POSITION (1-6) the setter occupies at the set's
// first serve, not a shirt number — resolve it via that set's positions map.
function setterShirtFromStartingLineup(startingLineup, side) {
  const pos = startingLineup?.[side]?.setterAt;
  if (pos == null) return null;
  const shirt = startingLineup?.[side]?.positions?.[String(pos)];
  return shirt != null ? parseInt(shirt, 10) : null;
}

// Smallest N (>=1) such that the first N characters of every name are
// pairwise distinct; falls back to the longest name's full length if even
// that doesn't disambiguate them (e.g. two identical first names).
function minimalPrefixLength(names) {
  const maxLen = Math.max(1, ...names.map((n) => n.length));
  for (let n = 1; n <= maxLen; n++) {
    const prefixes = names.map((n2) => n2.slice(0, n));
    if (new Set(prefixes).size === names.length) return n;
  }
  return maxLen;
}

// Builds the same display name a real DataVolley import shows (LastName,
// upper-cased) instead of VSM's own "code" field (e.g. "WIL-TOB") — and, when
// two team-mates share a last name, prefixes it with just enough of the first
// name's initial letters to tell them apart (e.g. "D SMITH"/"P SMITH" for
// David/Peter, but "DAV SMITH"/"DAR SMITH" for David/Darren).
function buildDisplayNames(players) {
  const byLast = new Map();
  players.forEach((p, idx) => {
    const last = String(p.lastName ?? "").trim().toUpperCase();
    const key = last || `__${idx}`;
    if (!byLast.has(key)) byLast.set(key, []);
    byLast.get(key).push(p);
  });
  const nameByPlayer = new Map();
  for (const group of byLast.values()) {
    const last = String(group[0].lastName ?? "").trim().toUpperCase();
    if (group.length === 1 || !last) {
      for (const p of group) {
        nameByPlayer.set(
          p,
          last || String(p.firstName ?? p.code ?? "").trim().toUpperCase()
        );
      }
      continue;
    }
    const firstNames = group.map((p) => String(p.firstName ?? "").trim().toUpperCase());
    const n = minimalPrefixLength(firstNames);
    group.forEach((p, i) => {
      const prefix = firstNames[i].slice(0, n);
      nameByPlayer.set(p, prefix ? `${prefix} ${last}` : last);
    });
  }
  return nameByPlayer;
}

function mapRoster(players, liberoShirts, setterShirt) {
  const roster = players ?? [];
  const displayNames = buildDisplayNames(roster);
  return roster.map((p) => {
    const shirt = p.shirtNumber;
    const isLibero = Array.isArray(liberoShirts) && liberoShirts.includes(shirt);
    return {
      shirtNumber: shirt,
      FirstName: p.firstName ?? "",
      LastName: p.lastName ?? "",
      NickName: displayNames.get(p) ?? (p.code ?? ""),
      role1: isLibero ? "L" : shirt === setterShirt ? "S" : "",
    };
  });
}

// VSM's team marker ("*" = home, "a" = away) onto our internal "A"/"B" keys.
function teamKeyFor(marker) {
  return marker === "*" ? "A" : "B";
}

function mapPlay(play, scoreA, scoreB, lineupSnapshot, setterPosA, setterPosB) {
  if (!play || play.player == null || play.player === "") return null;
  const shirt = parseInt(play.player, 10);
  if (!Number.isFinite(shirt)) return null;
  return {
    team: teamKeyFor(play.team),
    skill: play.skill,
    shirtNumber: shirt,
    evaluation: play.effect ?? "+",
    type: play.hitType && String(play.hitType).length === 1 ? play.hitType : "H",
    combination: play.combination ?? null,
    targetAttacker: play.targetAttacker ?? null,
    zoneFrom: play.startZone ?? null,
    zoneTo: play.endZone ?? null,
    subzoneTo: play.endSubZone ?? null,
    LineupSnapshot: lineupSnapshot,
    scoreA,
    scoreB,
    setterPosA,
    setterPosB,
    // VSM carries the Power Play call directly on the serve play ("HP"/"AP"/
    // "HAP"/"AHP" — see Constants.js kPowerPlayCodes), no position parsing needed.
    powerPlayCode:
      play.skill === "S" && kPowerPlayCodes.includes(play.custom) ? play.custom : null,
    // Generic custom-code passthrough (VSM's own "custom" field) -- written by
    // DVWWriter at the same fixed character-16+ position DVWFile.js reads
    // back as ev.customCode for native DVW files.
    customCode: play.custom ?? null,
    // "time" (not "originalTime") is the position in the video file the user
    // actually loads/plays in-app, in DECISECONDS (0.1s units) — confirmed
    // against real match video playback. "originalTime" tracks position in a
    // DIFFERENT, longer source recording (e.g. an untrimmed multi-match feed)
    // and does not match the user's own video file.
    VideoPosition: play.time != null ? play.time / 10 : undefined,
  };
}

export function vsmJsonToMatchData(vsm) {
  const teamHome = vsm?.team?.home ?? {};
  const teamAway = vsm?.team?.away ?? {};
  const scoutSets = vsm?.scout?.sets ?? [];

  const firstSet = scoutSets[0] ?? null;
  const setterA = firstSet
    ? setterShirtFromStartingLineup(firstSet.startingLineup, "home")
    : null;
  const setterB = firstSet
    ? setterShirtFromStartingLineup(firstSet.startingLineup, "away")
    : null;
  const liberoA = Array.isArray(teamHome.libero) ? teamHome.libero : [];
  const liberoB = Array.isArray(teamAway.libero) ? teamAway.libero : [];

  const sets = scoutSets.map((s) => {
    const events = [];
    (s.events ?? []).forEach((rally) => {
      const snapshot = lineupSnapshotFromRallyLineup(rally.lineup);
      // VSM's own "setterAt" (per rally, home/away) is the TRUE court position
      // of whoever is CURRENTLY acting setter — unlike inferring it from a
      // single tracked shirt's lineup slot, this stays correct even after a
      // mid-match setter substitution (a different player takes over the role
      // without the team's physical rotation order changing).
      const setterPosA = rally.lineup?.home?.setterAt ?? null;
      const setterPosB = rally.lineup?.away?.setterAt ?? null;
      if (rally.substitution) {
        const sub = rally.substitution;
        const out = parseInt(sub.playerOut, 10);
        const inNum = parseInt(sub.playerIn, 10);
        if (Number.isFinite(out) && Number.isFinite(inNum)) {
          events.push({
            team: teamKeyFor(sub.team),
            skill: "C",
            EventType: 250,
            substitution: `c${pad2(out)}:${pad2(inNum)}`,
            LineupSnapshot: snapshot,
            setterPosA,
            setterPosB,
          });
        }
        return;
      }
      const plays = rally.exchange?.plays ?? [];
      const scoreA = rally.score?.home ?? 0;
      const scoreB = rally.score?.away ?? 0;
      plays.forEach((play) => {
        const ev = mapPlay(play, scoreA, scoreB, snapshot, setterPosA, setterPosB);
        if (ev) events.push(ev);
      });
    });

    return {
      pointsA: s.score?.home ?? 0,
      pointsB: s.score?.away ?? 0,
      lineupA: lineupArrayFromPositions(s.startingLineup?.home?.positions),
      lineupB: lineupArrayFromPositions(s.startingLineup?.away?.positions),
      events,
    };
  });

  return {
    teamAName: teamHome.name || "Team A",
    teamBName: teamAway.name || "Team B",
    tournamentName: "",
    date: vsm?.startDate ? new Date(vsm.startDate) : new Date(),
    // `matchGuid` is a VBLive-specific extra field (not part of VolleyStation's
    // own schema) written by DVWToVSM.js's matchToVsmJson -- lets a match
    // converted DVW->VSM->DVW keep its original guid instead of minting a new
    // one every round trip (which would otherwise create a duplicate database
    // row rather than updating the existing match on save).
    guid: vsm?.matchGuid || generateUUID(),
    rosterA: mapRoster(teamHome.players, liberoA, setterA),
    rosterB: mapRoster(teamAway.players, liberoB, setterB),
    setterA,
    setterB,
    liberoA,
    liberoB,
    sets,
  };
}
